/**
 * Client-side email verification checkpoint for parent experiment sc-222848.
 *
 * Uses the parent GrowthBook flag and emailVerificationStatus query. Callers own
 * redirects and sticky-trap arming.
 *
 * Existing parents (`emailVerificationRequired = false`) skip placement entirely
 * and rely on GraphQL `showBanner` for the soft prompt.
 */
import { buildVerifyEmailPath, currentUserIsCorporate, currentUserIsParent } from '@/apps/verifyEmail/helpers/paths';
import { getEmailVerificationStatus } from '@/apps/verifyEmail/helpers/emailVerificationStatus';
import {
  getParentEmailVerificationPlacement,
  ParentEmailVerificationMoment,
  shouldRequireParentEmailVerificationNow,
} from '@/apps/verifyEmail/helpers/parentPlacementPolicy';
import { markEmailVerificationRequired } from '@/apps/verifyEmail/helpers/pendingEmailVerification';
import { redirect } from '@/helpers/redirects';

export type ParentEmailVerificationGateDecision =
  | { requireVerification: false }
  | { requireVerification: true; redirectTo: string };

export type DecideParentEmailVerificationGateInput = {
  verified: boolean;
  isParent: boolean;
  isCorporate?: boolean;
  emailVerificationRequired: boolean;
  placement: string;
  moment: ParentEmailVerificationMoment;
  returnPath?: string | null;
};

export function decideParentEmailVerificationGate(
  input: DecideParentEmailVerificationGateInput
): ParentEmailVerificationGateDecision {
  if (!shouldRequireParentEmailVerificationNow(input)) {
    return { requireVerification: false };
  }

  return {
    requireVerification: true,
    redirectTo: buildVerifyEmailPath(input.returnPath),
  };
}

export type ResolveParentEmailVerificationGateInput = {
  moment: ParentEmailVerificationMoment;
  returnPath?: string | null;
  isParent?: boolean;
  isCorporate?: boolean;
  fetchStatus?: () => Promise<{ verified: boolean; emailVerificationRequired: boolean }>;
  getPlacement?: () => string;
};

export async function resolveParentEmailVerificationGate(
  input: ResolveParentEmailVerificationGateInput
): Promise<ParentEmailVerificationGateDecision> {
  const isParent = input.isParent ?? currentUserIsParent();
  const isCorporate = input.isCorporate ?? currentUserIsCorporate();
  if (!isParent || isCorporate) {
    return { requireVerification: false };
  }

  let verified: boolean;
  let emailVerificationRequired: boolean;
  try {
    const status = input.fetchStatus ? await input.fetchStatus() : await getEmailVerificationStatus();
    verified = status.verified;
    emailVerificationRequired = status.emailVerificationRequired;
  } catch (error) {
    console.error('Error checking parent email verification status:', error);
    return { requireVerification: false };
  }

  // Existing soft-banner cohort: never evaluate GrowthBook placement.
  if (!emailVerificationRequired) {
    return { requireVerification: false };
  }

  const placement = input.getPlacement ? input.getPlacement() : getParentEmailVerificationPlacement();
  return decideParentEmailVerificationGate({
    verified,
    isParent,
    isCorporate,
    emailVerificationRequired,
    placement,
    moment: input.moment,
    returnPath: input.returnPath,
  });
}

export async function ensureParentEmailVerificationOrRedirect(
  moment: ParentEmailVerificationMoment,
  returnPath?: string | null,
  beforeRedirect?: () => boolean
): Promise<'proceed' | 'redirected' | 'blocked'> {
  const decision = await resolveParentEmailVerificationGate({ moment, returnPath });
  if (!decision.requireVerification) {
    return 'proceed';
  }

  if (beforeRedirect && !beforeRedirect()) {
    return 'blocked';
  }

  markEmailVerificationRequired(returnPath);
  redirect(decision.redirectTo);
  return 'redirected';
}
